import { useState } from 'react';

import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { KimThreadStatus } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { useDeleteKimThread } from '@/api/mutations/useDeleteKimThread';
import { useRenameKimThread } from '@/api/mutations/useRenameKimThread';
import { useGetKimThreads } from '@/api/queries/useGetKimThreads';
import { useUserContext } from '@/Contexts/User';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';

export function NewKimSidebar() {
  const { matterId, subsetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { data: threads, isLoading } = useGetKimThreads(matterId, subsetId);
  const deleteThread = useDeleteKimThread(matterId, subsetId);
  const renameThread = useRenameKimThread(matterId, subsetId);

  const [searchQuery, setSearchQuery] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingThread, setRenamingThread] = useState<{ id: string; name: string } | null>(null);

  const filteredThreads = threads?.filter((thread) => thread.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // threadId is not available from useParams because the sidebar sits outside the :threadId route
  const kimBasePath = `/matters/${matterId}/subsets/${subsetId}/new-kim/`;
  const activeThreadId = location.pathname.startsWith(kimBasePath)
    ? location.pathname.slice(kimBasePath.length).split('/')[0] || undefined
    : undefined;

  return (
    <div className="m-2 flex w-64 flex-col rounded-lg border bg-white p-3">
      <Button asChild size="sm" className="mb-2">
        <Link to={`/matters/${matterId}/subsets/${subsetId}/new-kim/`}>
          New Thread
          <Icon className="lucide--plus" />
        </Link>
      </Button>

      <div
        className={cn(
          'mb-2 flex items-center rounded-md border border-gray-300 bg-white ring-offset-2 transition-colors duration-150 focus-within:ring focus-within:ring-teal-500',
        )}
      >
        <Icon className="lucide--search mx-2.25 mr-1.5 text-xs text-gray-400" />
        <input
          type="text"
          placeholder="Search threads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full py-1.25 text-xs text-gray-900 outline-none placeholder:text-gray-400 focus:outline-none"
        />
      </div>

      {isLoading ? (
        <p className="my-8 animate-pulse text-center text-xs text-gray-400">Loading Threads...</p>
      ) : threads?.length === 0 ? (
        <p className="my-8 text-center text-xs text-gray-400">No threads yet</p>
      ) : searchQuery && filteredThreads?.length === 0 ? (
        <p className="my-8 text-center text-xs text-gray-400">No matching threads</p>
      ) : null}

      <div className="-m-1 flex grow flex-col gap-1 overflow-y-auto p-1">
        {filteredThreads?.map((thread) => (
          <div
            key={thread.id}
            className={cn(
              activeThreadId === thread.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600',
              'flex items-center justify-between rounded-md text-xs ring-offset-2 transition-colors hover:bg-gray-50 has-[>a:focus-visible]:ring-1 has-[>a:focus-visible]:ring-teal-500',
            )}
          >
            <Link
              to={`/matters/${matterId}/subsets/${subsetId}/new-kim/${thread.id}`}
              className={'w-full truncate px-2 py-1.5 pr-1 focus-visible:outline-none'}
            >
              {thread.name}
            </Link>

            {thread.status === KimThreadStatus.Failed && <Icon className="lucide--circle-alert mx-1.25 text-red-500" />}

            {thread.createdById === user?.id && (
              <DropdownMenu>
                <DropdownMenuTrigger className="mr-1 rounded-md px-1 ring-offset-2 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none">
                  <Icon className="lucide--more-horizontal" />{' '}
                </DropdownMenuTrigger>

                <DropdownMenuContent align="start" alignOffset={-8} side="right" sideOffset={6}>
                  <DropdownMenuItem
                    className="text-xs"
                    onClick={() => {
                      setRenamingThread({ id: thread.id, name: thread.name });
                      setRenameDialogOpen(true);
                    }}
                  >
                    Rename thread
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-xs"
                    onClick={() => {
                      deleteThread.mutate(thread.id, {
                        onSuccess: () => {
                          if (activeThreadId === thread.id) {
                            navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/`);
                          }
                        },
                      });
                    }}
                  >
                    Delete thread
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
      </div>

      {renamingThread && (
        <EditFieldDialogControlled
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          title="Rename thread"
          description="Enter a new name for this thread."
          fieldLabel="Name"
          currentValue={renamingThread.name}
          onSave={(name) => renameThread.mutate({ threadId: renamingThread.id, name })}
        />
      )}
    </div>
  );
}
